import {Accordion, Button, Checkbox} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {Text} from 'src/design/atoms/Text';
import React, {useMemo, useState} from 'react';
import type {
  ModuleEnum,
  TaskAssignmentBatchStatusByUserUpdateRequest,
  TaskAssignmentSimpleResponse,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {NoteModuleEnum, TaskAssignmentStateEnum} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {Textbox} from 'src/design/atoms/Textbox';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {NotesModule} from 'src/app/notes/NotesModule';
import {ConfirmActionButton} from 'src/design/molecules/ConfirmActionButton';
import {
  bulkTaskActionBody,
  TaskAssignmentPillConfig,
  TASK_ASSIGNMENT_OPEN_STATES,
  transitionAppliesToTaskType,
} from 'src/app/tasks/taskHelpers';
import {Paginator} from 'src/design/organisms/Pagination';
import {useListState} from 'src/design/organisms/useListState';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {
  useGetTaskAssignmentsListQuery,
  useGetTaskAssignmentsParticipationQuery,
  usePostBatchStatusUpdateByUserMutation,
} from 'src/redux/queries/taskAssignment';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useTaskPermissions} from 'src/shared/auth/permissions/useTaskPermissions';
import {useAlert} from 'src/shared/hooks/useAlert';
import useDebounce from 'src/shared/hooks/useDebounce';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {TaskParticipationBanner} from './TaskParticipationBanner';
import {TaskActiveView} from './taskInterfaces';

const views = [
  TaskActiveView.Incomplete,
  TaskActiveView.ReadyForReview,
  TaskActiveView.Complete,
];

const statesForView = (view: TaskActiveView): TaskAssignmentStateEnum[] => {
  switch (view) {
    case TaskActiveView.Complete:
      return [
        TaskAssignmentStateEnum.Completed,
        TaskAssignmentStateEnum.Cancelled,
      ];
    case TaskActiveView.ReadyForReview:
      return [TaskAssignmentStateEnum.Submitted];
    case TaskActiveView.Incomplete:
      return TASK_ASSIGNMENT_OPEN_STATES;
    default:
      return [];
  }
};

export interface AdminTaskAssignmentsListByUserProps {
  userId: number;
  taskType: TaskTypeEnum;
  module: ModuleEnum;
  onTransition?: () => Promise<void>;
}

export const AdminTaskAssignmentsListByUser: React.FC<
  AdminTaskAssignmentsListByUserProps
> = ({userId, taskType, module, onTransition}) => {
  const maxPageRows = 20;
  const {setAlert} = useAlert();

  const taskPerms = useTaskPermissions(module);
  const [assignmentStateMachine] = useStateMachine({
    url: ApiEndpoints.getTaskAssignmentStateMachine,
  });

  const [hasSelectedAll, setHasSelectedAll] = useState<boolean>(false);
  const [selected, setSelected] = useState<number[]>([]);
  const {
    searchTerm: search,
    setSearchTerm: setSearch,
    filters,
    setFilters,
    currentPage,
    setCurrentPage,
    start,
    length,
  } = useListState({
    identifier: `admin-task-assignments-by-user-${taskType}-${userId}`,
    pageSize: maxPageRows,
    defaultFilters: {activeView: TaskActiveView.Incomplete},
  });
  const debouncedSearch = useDebounce(search, 500);

  const activeView = views.includes(filters.activeView)
    ? filters.activeView
    : TaskActiveView.Incomplete;
  const states = useMemo(() => statesForView(activeView), [activeView]);

  const {
    data: listData,
    isLoading: isListLoading,
    error: listError,
  } = useGetTaskAssignmentsListQuery({
    assignedToUserId: userId,
    taskType: taskType,
    assignmentStates: states,
    start,
    length,
    search: debouncedSearch,
  });

  const {
    data: participation,
    isLoading: isStatsLoading,
    error: statsError,
  } = useGetTaskAssignmentsParticipationQuery({userId, taskType});

  const [postBatchStatusUpdateByUser, {isLoading: isSaving}] =
    usePostBatchStatusUpdateByUserMutation();

  if (listError) {
    setAlert(parseErrorMessage(listError), 'error');
  }
  if (statsError) {
    setAlert(parseErrorMessage(statsError), 'error');
  }

  const displayData: TaskAssignmentSimpleResponse[] = listData?.data ?? [];
  const totalRecords = listData?.totalRecords ?? 0;
  const isLoading = isListLoading || isStatsLoading;

  const submitBulkTransition = async (transition: string) => {
    const selectedIDs = selected.map(item => item);

    try {
      const request: TaskAssignmentBatchStatusByUserUpdateRequest = {
        userId: userId,
        transition: transition,
        selectedAll: hasSelectedAll,
        taskAssignmentIds: selectedIDs,
      };

      await postBatchStatusUpdateByUser(request).unwrap();

      setAlert(`Selected items successfully updated.`, 'success');
      setSelected([]);
      setHasSelectedAll(false);
      if (onTransition) {
        void onTransition();
      }
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    e.stopPropagation();

    const {checked} = e.target;

    if (checked) {
      setSelected(prevState => [...prevState, id]);
    } else {
      setSelected(prevState => prevState.filter(i => i != id));
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {checked} = e.target;

    setHasSelectedAll(checked);

    if (checked === false) {
      setSelected([]);
    }
  };

  const handleMarkAsCancelled = async () => {
    await submitBulkTransition('cancel');
  };
  const handleMarkAsCompleted = async () => {
    await submitBulkTransition('complete');
  };
  const handleMarkAsApproved = async () => {
    await submitBulkTransition('approve');
  };
  const handleMarkAsReturned = async () => {
    await submitBulkTransition('return-for-rework');
  };

  const handleOptionKeyClick = (value: TaskActiveView) => {
    setSelected([]);
    setHasSelectedAll(false);
    setFilters({activeView: value});
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setSelected([]);
    setHasSelectedAll(false);
    setCurrentPage(page);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setSearch(value);
  };

  const selectedDisplayCount = hasSelectedAll ? totalRecords : selected.length;

  return isLoading === false ? (
    <Box>
      <TaskParticipationBanner item={participation} />

      <Group justify="space-between" align="flex-start">
        <SegmentedControl
          value={String(activeView)}
          onChange={val => handleOptionKeyClick(Number(val) as TaskActiveView)}
          aria-label="Assignment Status"
          data={views.map(view => ({
            value: String(view),
            label:
              view === TaskActiveView.Incomplete
                ? `Incomplete (${participation?.incomplete})`
                : view === TaskActiveView.Complete
                ? `Complete (${participation?.completed})`
                : `Ready for Review (${participation?.pendingReview})`,
            disabled:
              (hasSelectedAll && activeView !== view) ||
              (search?.length > 0 && activeView !== view),
          }))}
        />
        <Box>
          {taskPerms.update && (
            <>
              {activeView === TaskActiveView.ReadyForReview && (
                <>
                  <ConfirmActionButton
                    body={bulkTaskActionBody('approve', selectedDisplayCount)}
                    onConfirm={handleMarkAsApproved}
                    loading={isSaving}
                    renderButton={(onClick, loading) => (
                      <Button
                        color="green"
                        mr="md"
                        loading={loading}
                        disabled={
                          selectedDisplayCount === 0 ||
                          activeView !== TaskActiveView.ReadyForReview
                        }
                        onClick={
                          onClick
                        }>{`Mark ${selectedDisplayCount} as Approved`}</Button>
                    )}
                  />
                  <ConfirmActionButton
                    body={bulkTaskActionBody(
                      'return-for-rework',
                      selectedDisplayCount
                    )}
                    onConfirm={handleMarkAsReturned}
                    loading={isSaving}
                    renderButton={(onClick, loading) => (
                      <Button
                        color="red"
                        mr="md"
                        loading={loading}
                        disabled={
                          selectedDisplayCount === 0 ||
                          activeView !== TaskActiveView.ReadyForReview
                        }
                        onClick={
                          onClick
                        }>{`Mark ${selectedDisplayCount} as Returned`}</Button>
                    )}
                  />
                </>
              )}
              {activeView === TaskActiveView.Incomplete &&
                transitionAppliesToTaskType(
                  assignmentStateMachine,
                  'complete',
                  taskType
                ) && (
                  <ConfirmActionButton
                    body={bulkTaskActionBody('complete', selectedDisplayCount)}
                    onConfirm={handleMarkAsCompleted}
                    loading={isSaving}
                    renderButton={(onClick, loading) => (
                      <Button
                        mr="md"
                        loading={loading}
                        disabled={selectedDisplayCount === 0}
                        onClick={
                          onClick
                        }>{`Mark ${selectedDisplayCount} as Completed`}</Button>
                    )}
                  />
                )}
              {activeView !== TaskActiveView.Complete && (
                <ConfirmActionButton
                  body={bulkTaskActionBody('cancel', selectedDisplayCount)}
                  onConfirm={handleMarkAsCancelled}
                  loading={isSaving}
                  renderButton={(onClick, loading) => (
                    <Button
                      mr="md"
                      loading={loading}
                      disabled={selectedDisplayCount === 0}
                      onClick={
                        onClick
                      }>{`Mark ${selectedDisplayCount} as Cancelled`}</Button>
                  )}
                />
              )}
            </>
          )}
        </Box>
      </Group>

      <Group justify="space-between" align="flex-start" mt="lg">
        <CheckboxRow
          label={`select all (${selectedDisplayCount} selected)`}
          disabled={
            activeView === TaskActiveView.Complete ||
            isSaving ||
            !taskPerms.update
          }
          checked={hasSelectedAll}
          onChange={handleSelectAll}
        />
        <Textbox
          label="Search"
          ariaLabel="searchIndex"
          name="search"
          value={search}
          onChangeCallback={handleSearch}
        />
      </Group>

      <Paginator
        mt={16}
        mb={16}
        currentPage={currentPage}
        totalRecords={totalRecords}
        perPage={maxPageRows}
        showRecordCount={false}
        onChange={handlePageChange}
      />
      {displayData.length > 0 ? (
        <Accordion>
          {displayData.map(item => {
            const isCompleted =
              item.state === TaskAssignmentStateEnum.Completed ||
              item.state === TaskAssignmentStateEnum.Cancelled;
            return (
              <Accordion.Item key={item.id} value={String(item.id)}>
                <Center>
                  {!isCompleted && (
                    <Checkbox
                      id={item.id.toString()}
                      ml={8}
                      disabled={isSaving || !taskPerms.update}
                      checked={selected.includes(item.id) || hasSelectedAll}
                      onChange={e => handleSelect(e, item.id)}
                    />
                  )}
                  <Accordion.Control>
                    <Group>
                      <Text size="sm" style={{minWidth: 170}}>
                        {item.taskDefinition.subject}
                      </Text>
                      <Box ml="lg">
                        {TaskAssignmentPillConfig(
                          item.state,
                          item.taskDefinition.taskType,
                          item.taskDefinition.state,
                          item.taskDefinition.dueAt
                        )?.map(pill => {
                          return <Pill key={pill.text} {...pill} />;
                        })}
                      </Box>
                    </Group>
                  </Accordion.Control>
                </Center>
                <Accordion.Panel>
                  <NotesModule
                    entityId={item.id}
                    noteType={NoteModuleEnum.TaskAssignment}
                  />
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      ) : (
        <Box pt="xl">No selections found</Box>
      )}
    </Box>
  ) : (
    <TableBodyRender isLoading={isLoading} />
  );
};
